import { supabase } from "@/integrations/supabase/client";
import { ProductDescription } from "@/lib/api";
import { operationQueue } from "@/utils/operationQueue";
import { runConsistencyCheck } from "@/lib/api/consistency";
import { requestCache } from "@/utils/requestCache";
import { manualSyncProductGroup } from "@/utils/manualProductSync";

/**
 * Centralized service for product description operations
 * Uses the operation queue to prevent race conditions
 */
export const productService = {
  /**
   * Improves a product description and ensures consistency across variants
   */
  improveDescription: async (description: ProductDescription): Promise<boolean> => {
    if (!description.id || !description.collection_id) {
      throw new Error('Missing required fields: id or collection_id');
    }

    const operationKey = description.content_id 
      ? `improve-group-${description.content_id}` 
      : `improve-product-${description.id}`;
    
    // Enqueue this operation to prevent race conditions
    const result = await operationQueue.enqueue(operationKey, async () => {
      console.log(`Starting improvement process for ${description.id}`);
      
      // Set status to improving for UI feedback
      if (description.content_id) {
        const { data: variants, error: variantsError } = await supabase
          .from("product_descriptions")
          .select("id")
          .eq("content_id", description.content_id)
          .eq("collection_id", description.collection_id);
        
        if (variantsError) throw variantsError;
        
        if (variants && variants.length > 0) {
          const { error } = await supabase
            .from("product_descriptions")
            .update({ status: "improving" })
            .in("id", variants.map(v => v.id));

          if (error) throw error;
        }
      } else {
        // Just update this single product
        const { error } = await supabase
          .from("product_descriptions")
          .update({ status: "improving" })
          .eq("id", description.id);

        if (error) throw error;
      }
      
      // Use the text as-is without AI processing
      const improvedText = description.generated_description || description.original_text;
      
      if (description.content_id) {
        // For group members, ensure all products get the same update using a database function
        const { data: syncResult, error: syncError } = await supabase
          .rpc('sync_product_group', {
            p_content_id: description.content_id,
            p_description: improvedText,
            p_collection_id: description.collection_id,
            p_source_product_id: description.id
          });
          
        if (syncError) throw syncError;
      } else {
        // Single product update
        const { error: updateError } = await supabase
          .from("product_descriptions")
          .update({ 
            improved_description: improvedText,
            status: "pending"
          })
          .eq("id", description.id);
          
        if (updateError) throw updateError;
      }
      
      return true;
    });
    
    return result.success ? true : false;
  },
  
  /**
   * Edit a description with user-provided text
   * Ensures consistency across product groups
   */
  editDescription: async (description: ProductDescription, editedText: string, originalText?: string): Promise<boolean> => {
    // Input validation to catch issues early
    if (!description || !description.id) {
      console.error("[EDIT DEBUG] Missing or invalid description object", description);
      return false;
    }
    
    if (!editedText) {
      console.error("[EDIT DEBUG] Missing edited text, cannot proceed");
      return false;
    }
    
    const operationKey = description.content_id
      ? `edit-group-${description.content_id}`
      : `edit-product-${description.id}`;
    
    // Clear any cache to ensure fresh data
    try {
      console.log(`[EDIT DEBUG] Starting edit operation for ${description.id}`, {
        hasContentId: !!description.content_id,
        contentId: description.content_id,
        collectionId: description.collection_id,
        editedText: editedText?.substring(0, 50) + '...',
        originalText: originalText?.substring(0, 50) + '...',
        isGroup: !!description.content_id
      });
      
      const result = await operationQueue.enqueue(operationKey, async () => {
        console.log(`[EDIT DEBUG] Editing description ${description.id} with text:`, editedText?.substring(0, 30) + '...');
        console.log(`[EDIT DEBUG] Original text to update:`, originalText?.substring(0, 30) + '...');
        
        if (!editedText) {
          throw new Error('Missing edited description text');
        }
        
        const updateData: Record<string, any> = { 
          improved_description: editedText,
          status: "pending" // Reset status when edited
        };
        
        // CRITICAL: Always include original_text if provided
        if (originalText !== undefined) {
          updateData.original_text = originalText;
          console.log("[EDIT DEBUG] Including original_text in update:", originalText?.substring(0, 30) + '...');
        } else {
          console.warn("[EDIT DEBUG] No original_text provided for update! Using existing value.");
        }
        
        // First, update the current product regardless of whether it's part of a group
        console.log("[EDIT DEBUG] Updating current product first:", description.id);
        const { error: directUpdateError } = await supabase
          .from("product_descriptions")
          .update(updateData)
          .eq("id", description.id);
        
        if (directUpdateError) {
          console.error("[EDIT DEBUG] Error updating current product:", directUpdateError);
          throw directUpdateError;
        }
        
        // If it's part of a content group, sync changes to all other products in the group
        if (description.content_id) {
          console.log(`[EDIT DEBUG] This is a group edit for content_id: ${description.content_id}`);
          
          try {
            // Use the improved manual sync method with enhanced debugging
            console.log("[EDIT DEBUG] Using manual sync approach with original text:", originalText?.substring(0, 30) + "...");
            const syncResult = await manualSyncProductGroup(description, editedText, originalText);
            
            if (!syncResult.success) {
              console.warn("[EDIT DEBUG] Manual sync was not fully successful");
              
              // Try alternative method with RPC function
              console.log("[EDIT DEBUG] Falling back to RPC function sync_product_group_atomic");
              try {
                const { data: rpcResult, error: rpcError } = await supabase.rpc('sync_product_group_atomic', {
                  p_content_id: description.content_id,
                  p_description: editedText,
                  p_collection_id: description.collection_id,
                  p_source_product_id: description.id,
                  p_original_text: originalText
                });
                
                if (rpcError) {
                  console.error("[EDIT DEBUG] RPC function error:", rpcError);
                } else {
                  console.log("[EDIT DEBUG] RPC function success:", rpcResult);
                }
              } catch (rpcCallError) {
                console.error("[EDIT DEBUG] Error calling RPC function:", rpcCallError);
              }
            } else {
              console.log(`[EDIT DEBUG] Manual sync updated ${syncResult.count} products successfully`);
            }
          } catch (syncError) {
            console.error("[EDIT DEBUG] Error with manual sync:", syncError);
            // Continue anyway - at least the current product was updated
          }
        } else {
          console.log(`[EDIT DEBUG] This is a single product edit for ID: ${description.id}`);
        }
        
        // Clear any cache to ensure fresh data
        requestCache.invalidateCollection(description.collection_id);
        if (description.content_id) {
          requestCache.invalidateContentGroup(description.content_id);
          
          // Explicitly invalidate specific query keys
          requestCache.invalidateQueryKeys([
            ["productDescriptions", description.collection_id],
            ["descriptions", description.collection_id],
            ["collectionStats"]
          ]);
        }
        requestCache.clear();
        
        return true;
      });
      
      console.log("[EDIT DEBUG] Operation complete, result:", result);
      return result.success;
    } catch (error) {
      console.error("[EDIT DEBUG] Critical error in editDescription:", error);
      return false;
    }
  },
  
  /**
   * Approve a description (and optionally its variants)
   */
  approveDescription: async (description: ProductDescription): Promise<boolean> => {
    if (!description || !description.id) {
      console.error("[APPROVE] Missing or invalid description", description);
      return false;
    }
    
    const operationKey = description.content_id
      ? `approve-group-${description.content_id}`
      : `approve-product-${description.id}`;
    
    console.log(`[APPROVE] Starting approval operation for ${description.id} with operation key ${operationKey}`);
    
    const result = await operationQueue.enqueue(operationKey, async () => {
      try {
        if (description.content_id) {
          // Approve ONLY products in this specific content group - not the entire collection
          console.log(`[APPROVE] Approving content group ${description.content_id}`);
          
          const { data: groupProducts, error: groupError } = await supabase
            .from("product_descriptions")
            .select("id")
            .eq("content_id", description.content_id)
            .eq("collection_id", description.collection_id);
            
          if (groupError) {
            console.error("[APPROVE] Error selecting group products:", groupError);
            throw groupError;
          }
          
          if (groupProducts && groupProducts.length > 0) {
            const groupIds = groupProducts.map(item => item.id);
            console.log(`[APPROVE] Found ${groupIds.length} products in content group to approve:`, groupIds);
            
            const { error } = await supabase
              .from("product_descriptions")
              .update({ status: "approved" })
              .in("id", groupIds);
              
            if (error) {
              console.error("[APPROVE] Error updating group products:", error);
              throw error;
            }
            
            console.log(`[APPROVE] Successfully approved ${groupIds.length} products in group`);
          } else {
            console.log("[APPROVE] No group products found to approve");
          }
        } else {
          // Approve ONLY this specific product
          console.log(`[APPROVE] Approving single product ${description.id}`);
          
          const { error } = await supabase
            .from("product_descriptions")
            .update({ status: "approved" })
            .eq("id", description.id);
            
          if (error) {
            console.error("[APPROVE] Error updating single product:", error);
            throw error;
          }
          
          console.log(`[APPROVE] Successfully approved product ${description.id}`);
        }
        
        return true;
      } catch (error) {
        console.error("[APPROVE] Operation failed:", error);
        return false;
      }
    });
    
    return result.success;
  },
  
  /**
   * Revoke approval for a description (and optionally its variants)
   */
  revokeApproval: async (description: ProductDescription): Promise<boolean> => {
    if (!description || !description.id) {
      console.error("[REVOKE] Missing or invalid description", description);
      return false;
    }
    
    const operationKey = description.content_id
      ? `revoke-group-${description.content_id}`
      : `revoke-product-${description.id}`;
    
    console.log(`[REVOKE] Starting revoke operation for ${description.id} with operation key ${operationKey}`);
    
    const result = await operationQueue.enqueue(operationKey, async () => {
      try {
        if (description.content_id) {
          // Revoke approval for ONLY products in this specific content group - not the entire collection
          console.log(`[REVOKE] Revoking content group ${description.content_id}`);
          
          const { data: groupProducts, error: groupError } = await supabase
            .from("product_descriptions")
            .select("id")
            .eq("content_id", description.content_id)
            .eq("collection_id", description.collection_id)
            .eq("status", "approved");  // Only target approved products
            
          if (groupError) {
            console.error("[REVOKE] Error selecting group products:", groupError);
            throw groupError;
          }
          
          if (groupProducts && groupProducts.length > 0) {
            const groupIds = groupProducts.map(item => item.id);
            console.log(`[REVOKE] Found ${groupIds.length} approved products in content group to revoke:`, groupIds);
            
            const { error } = await supabase
              .from("product_descriptions")
              .update({ status: "pending" })
              .in("id", groupIds);
              
            if (error) {
              console.error("[REVOKE] Error updating group products:", error);
              throw error;
            }
            
            console.log(`[REVOKE] Successfully revoked approval for ${groupIds.length} products in group`);
          } else {
            console.log("[REVOKE] No approved group products found to revoke");
          }
        } else {
          // Revoke approval for ONLY this specific product
          console.log(`[REVOKE] Revoking approval for single product ${description.id}`);
          
          const { error } = await supabase
            .from("product_descriptions")
            .update({ status: "pending" })
            .eq("id", description.id)
            .eq("status", "approved");  // Only target if currently approved
            
          if (error) {
            console.error("[REVOKE] Error updating single product:", error);
            throw error;
          }
          
          console.log(`[REVOKE] Successfully revoked approval for product ${description.id}`);
        }
        
        return true;
      } catch (error) {
        console.error("[REVOKE] Operation failed:", error);
        return false;
      }
    });
    
    return result.success;
  },
  
  /**
   * Delete a description
   */
  deleteDescription: async (description: ProductDescription): Promise<boolean> => {
    const operationKey = `delete-product-${description.id}`;
    
    const result = await operationQueue.enqueue(operationKey, async () => {
      // Check if this is the last product in a content group
      if (description.content_id) {
        const { count, error: countError } = await supabase
          .from("product_descriptions")
          .select("id", { count: "exact", head: true })
          .eq("content_id", description.content_id)
          .eq("collection_id", description.collection_id);
          
        if (countError) throw countError;
        
        // If this is the last product in the group, remove content_id from related translations
        if (count === 1) {
          const { error: translationError } = await supabase
            .from("translated_descriptions")
            .update({ content_id: null })
            .eq("content_id", description.content_id);
            
          if (translationError) {
            console.error("Error updating translations:", translationError);
            // Continue anyway as this is not critical
          }
        }
      }
      
      // Delete the product description
      const { error } = await supabase
        .from("product_descriptions")
        .delete()
        .eq("id", description.id);
        
      if (error) throw error;
      
      return true;
    });
    
    return result.success;
  },
  
  /**
   * Fix inconsistent groups in a collection
   */
  fixInconsistentGroups: async (collectionId: string) => {
    const operationKey = `fix-groups-${collectionId}`;
    
    const result = await operationQueue.enqueue(operationKey, async () => {
      return await runConsistencyCheck(collectionId);
    });
    
    return result.success ? (result.data || { fixed: 0, groups: 0 }) : { fixed: 0, groups: 0 };
  },
  
  /**
   * Fast bulk approve multiple descriptions - optimized version that bypasses operation queue
   */
  bulkApproveDescriptions: async (descriptions: ProductDescription[]): Promise<{
    success: boolean;
    count: number;
    error?: string;
  }> => {
    if (!descriptions || descriptions.length === 0) {
      return { success: false, count: 0, error: "No descriptions provided" };
    }

    console.log(`[FAST BULK APPROVE] Starting optimized bulk approve for ${descriptions.length} items`);
    
    try {
      // Get all content_ids that need group expansion
      const contentIds = [...new Set(descriptions
        .filter(desc => desc.content_id)
        .map(desc => desc.content_id)
      )];
      
      let allTargetIds: string[] = [];
      
      // If we have content groups, expand them to include all group members
      if (contentIds.length > 0) {
        console.log(`[FAST BULK APPROVE] Expanding ${contentIds.length} content groups`);
        
        const { data: groupMembers, error: groupError } = await supabase
          .from("product_descriptions")
          .select("id")
          .in("content_id", contentIds)
          .eq("status", "pending");
          
        if (groupError) {
          console.error("[FAST BULK APPROVE] Error fetching group members:", groupError);
          throw groupError;
        }
        
        const groupIds = groupMembers?.map(member => member.id) || [];
        allTargetIds.push(...groupIds);
        console.log(`[FAST BULK APPROVE] Found ${groupIds.length} items in content groups`);
      }
      
      // Add standalone items (those without content_id)
      const standaloneIds = descriptions
        .filter(desc => !desc.content_id)
        .map(desc => desc.id);
      
      allTargetIds.push(...standaloneIds);
      console.log(`[FAST BULK APPROVE] Added ${standaloneIds.length} standalone items`);
      
      // Remove duplicates
      allTargetIds = [...new Set(allTargetIds)];
      console.log(`[FAST BULK APPROVE] Total unique items to approve: ${allTargetIds.length}`);
      
      if (allTargetIds.length === 0) {
        return { success: true, count: 0 };
      }
      
      // Single bulk update for all items
      const { error } = await supabase
        .from("product_descriptions")
        .update({ status: "approved" })
        .in("id", allTargetIds)
        .eq("status", "pending"); // Only approve pending items
        
      if (error) {
        console.error("[FAST BULK APPROVE] Error in bulk update:", error);
        throw error;
      }
      
      console.log(`[FAST BULK APPROVE] Successfully approved ${allTargetIds.length} items`);
      
      return { 
        success: true, 
        count: allTargetIds.length
      };
    } catch (error: any) {
      console.error("[FAST BULK APPROVE] Operation failed:", error);
      return { 
        success: false, 
        count: 0, 
        error: error.message || "Unknown error occurred" 
      };
    }
  },
  
  /**
   * Fast bulk revoke approval for multiple descriptions - optimized version that bypasses operation queue
   */
  bulkRevokeDescriptions: async (descriptions: ProductDescription[]): Promise<{
    success: boolean;
    count: number;
    error?: string;
  }> => {
    if (!descriptions || descriptions.length === 0) {
      return { success: false, count: 0, error: "No descriptions provided" };
    }

    console.log(`[FAST BULK REVOKE] Starting optimized bulk revoke for ${descriptions.length} items`);
    
    try {
      // Get all content_ids that need group expansion
      const contentIds = [...new Set(descriptions
        .filter(desc => desc.content_id)
        .map(desc => desc.content_id)
      )];
      
      let allTargetIds: string[] = [];
      
      // If we have content groups, expand them to include all group members
      if (contentIds.length > 0) {
        console.log(`[FAST BULK REVOKE] Expanding ${contentIds.length} content groups`);
        
        const { data: groupMembers, error: groupError } = await supabase
          .from("product_descriptions")
          .select("id")
          .in("content_id", contentIds)
          .eq("status", "approved");
          
        if (groupError) {
          console.error("[FAST BULK REVOKE] Error fetching group members:", groupError);
          throw groupError;
        }
        
        const groupIds = groupMembers?.map(member => member.id) || [];
        allTargetIds.push(...groupIds);
        console.log(`[FAST BULK REVOKE] Found ${groupIds.length} items in content groups`);
      }
      
      // Add standalone items (those without content_id)
      const standaloneIds = descriptions
        .filter(desc => !desc.content_id)
        .map(desc => desc.id);
      
      allTargetIds.push(...standaloneIds);
      console.log(`[FAST BULK REVOKE] Added ${standaloneIds.length} standalone items`);
      
      // Remove duplicates
      allTargetIds = [...new Set(allTargetIds)];
      console.log(`[FAST BULK REVOKE] Total unique items to revoke: ${allTargetIds.length}`);
      
      if (allTargetIds.length === 0) {
        return { success: true, count: 0 };
      }
      
      // Single bulk update for all items
      const { error } = await supabase
        .from("product_descriptions")
        .update({ status: "pending" })
        .in("id", allTargetIds)
        .eq("status", "approved"); // Only revoke approved items
        
      if (error) {
        console.error("[FAST BULK REVOKE] Error in bulk update:", error);
        throw error;
      }
      
      console.log(`[FAST BULK REVOKE] Successfully revoked ${allTargetIds.length} items`);
      
      return { 
        success: true, 
        count: allTargetIds.length
      };
    } catch (error: any) {
      console.error("[FAST BULK REVOKE] Operation failed:", error);
      return { 
        success: false, 
        count: 0, 
        error: error.message || "Unknown error occurred" 
      };
    }
  }
};
